import React, { useMemo, useState } from "react";
import {
  Box,
  Collapse,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { Section } from "../commercial-dashboard/DashboardBits";
import {
  CHANNELS,
  MONTHS_SHORT,
  groupBy,
  keur,
  mean,
  median,
  modelLabel,
  subformatLabel,
  sum,
  unitMoney,
} from "./controlBoardModel";
import { Caret, ChannelSwatch, Note, Num } from "./ControlBoardBits";
import { TabProps } from "./boardData";
import { ControlBoardChannel, ControlBoardRow } from "../../types/controlBoard";

/** Modelos de precio por volumen: los únicos comparables entre campañas. */
export const PRICE_MODELS = ["CPM", "CPC", "CPV"];

/**
 * Precios de compra de medios del año: líneas con precio de venta en CPM, CPC o CPV, sin las
 * que se salen de escala (más de 3 veces por encima o por debajo del habitual de su
 * subformato), que se apartan para revisarlas.
 */
export function cleanPrices(rows: ControlBoardRow[]) {
  // Una línea por línea de briefing: el precio no cambia de un mes a otro.
  const lines = [...groupBy(rows, (r) => r.lineId).values()].map((list) => ({
    ...list[0],
    sale: sum(list, (r) => r.sale),
    realCost: sum(list, (r) => r.realCost),
    volume: sum(list, (r) => r.volume),
    plannedVolume: sum(list, (r) => r.plannedVolume),
  }));
  const priced = lines.filter(
    (r) =>
      r.type === "CM" &&
      r.grossUnitCost > 0 &&
      PRICE_MODELS.includes(r.objective)
  );
  const medians = new Map<string, number>();
  groupBy(priced, (r) => `${r.channel}|${r.subformat}|${r.objective}`).forEach(
    (list, key) => medians.set(key, median(list.map((r) => r.grossUnitCost)))
  );
  const habitual = (r: ControlBoardRow) =>
    medians.get(`${r.channel}|${r.subformat}|${r.objective}`) ?? NaN;
  const ok: ControlBoardRow[] = [];
  const review: ControlBoardRow[] = [];
  priced.forEach((r) => {
    const m = habitual(r);
    (r.grossUnitCost > 3 * m || r.grossUnitCost < m / 3 ? review : ok).push(r);
  });
  return { ok, review, habitual };
}

/** Precios: a cuánto se vende cada producto y a cada cliente. */
const PricesTab: React.FC<TabProps> = ({ data }) => {
  const theme = useTheme();
  const [channel, setChannel] = useState<ControlBoardChannel | "">("");
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [openMatrix, setOpenMatrix] = useState<Set<string>>(new Set());
  const [showReview, setShowReview] = useState(false);
  const { ok, review, habitual } = useMemo(
    () => cleanPrices(data.rows),
    [data]
  );
  const rows = ok.filter((r) => !channel || r.channel === channel);
  const channels = CHANNELS.filter((c) => ok.some((r) => r.channel === c));
  const groups = [
    ...groupBy(rows, (r) => `${r.channel}|${r.objective}`).entries(),
  ]
    .map(([key, list]) => ({
      key,
      channel: list[0].channel,
      objective: list[0].objective,
      list,
    }))
    .sort((a, b) => sum(b.list, (r) => r.sale) - sum(a.list, (r) => r.sale));
  const clients = [...groupBy(rows, (r) => r.client).entries()]
    .map(([name, list]) => ({ name, sale: sum(list, (r) => r.sale) }))
    .sort((a, b) => b.sale - a.sale)
    .map((c) => c.name);

  const toggle = (
    set: Set<string>,
    setter: (s: Set<string>) => void,
    key: string
  ) => {
    const next = new Set(set);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setter(next);
  };

  const summaryRow = (
    label: React.ReactNode,
    list: ControlBoardRow[],
    indent: number,
    attrs?: { onClick?: () => void; bg?: string }
  ) => {
    const prices = list.map((r) => r.grossUnitCost);
    return (
      <TableRow
        hover={!!attrs?.onClick}
        onClick={attrs?.onClick}
        sx={{
          cursor: attrs?.onClick ? "pointer" : "default",
          backgroundColor: attrs?.bg,
        }}
      >
        <TableCell sx={{ pl: indent }}>{label}</TableCell>
        <Num>{list.length}</Num>
        <Num>{new Set(list.map((r) => r.clientId)).size}</Num>
        <Num muted>{keur(sum(list, (r) => r.sale))}</Num>
        <Num sx={{ fontWeight: 600 }}>{unitMoney(mean(prices))}</Num>
        <Num>{unitMoney(Math.min(...prices))}</Num>
        <Num>{unitMoney(Math.max(...prices))}</Num>
      </TableRow>
    );
  };

  const matrixRow = (
    label: React.ReactNode,
    list: ControlBoardRow[],
    indent: number,
    key: string,
    bg?: string,
    onClick?: () => void
  ) => {
    const byClient = groupBy(list, (r) => r.client);
    return (
      <TableRow key={key} sx={{ backgroundColor: bg }}>
        <TableCell
          onClick={onClick}
          sx={{
            pl: indent,
            position: "sticky",
            left: 0,
            zIndex: 1,
            backgroundColor: bg ?? theme.palette.background.paper,
            cursor: onClick ? "pointer" : "default",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </TableCell>
        <TableCell
          align="center"
          sx={{ fontWeight: 600, whiteSpace: "nowrap" }}
        >
          {unitMoney(mean(list.map((r) => r.grossUnitCost)))}
          <Typography variant="caption" color="text.secondary" display="block">
            {list.length} líneas
          </Typography>
        </TableCell>
        {clients.map((c) => {
          const l = byClient.get(c) ?? [];
          if (!l.length)
            return (
              <TableCell key={c} align="center" sx={{ color: "text.disabled" }}>
                ·
              </TableCell>
            );
          const prices = l.map((r) => r.grossUnitCost);
          const min = Math.min(...prices);
          const max = Math.max(...prices);
          return (
            <TableCell
              key={c}
              align="center"
              title={`${l.length} ${l.length === 1 ? "línea" : "líneas"}${max > min * 1.005 ? ` · de ${unitMoney(min)} a ${unitMoney(max)}` : ""}`}
              sx={{ whiteSpace: "nowrap", fontSize: 12, px: 0.5 }}
            >
              {unitMoney(mean(prices))}
            </TableCell>
          );
        })}
      </TableRow>
    );
  };

  return (
    <>
      <Section
        title="Precios por producto"
        subtitle="Precio unitario de venta de cada producto en compra de medios: media simple de las líneas, el más bajo y el más alto. Solo modelos por volumen (CPM, CPC y CPV); en CPM el precio es por mil."
        action={
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="pr-f">Formato</InputLabel>
            <Select
              labelId="pr-f"
              label="Formato"
              value={channel}
              onChange={(e) =>
                setChannel(e.target.value as ControlBoardChannel | "")
              }
            >
              <MenuItem value="">Todos</MenuItem>
              {channels.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        }
      >
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Producto</TableCell>
                <Num>Líneas</Num>
                <Num>Clientes</Num>
                <Num>Inversión</Num>
                <Num>Precio medio</Num>
                <Num>Precio mínimo</Num>
                <Num>Precio máximo</Num>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((g) => (
                <React.Fragment key={g.key}>
                  {summaryRow(
                    <>
                      <Caret open={open.has(g.key)} />
                      <ChannelSwatch channel={g.channel} />
                      <strong>{g.channel}</strong>{" "}
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                      >
                        {modelLabel(g.objective)}
                      </Typography>
                    </>,
                    g.list,
                    1,
                    { onClick: () => toggle(open, setOpen, g.key) }
                  )}
                  {open.has(g.key) &&
                    [...groupBy(g.list, (r) => r.subformat).entries()]
                      .sort(
                        (a, b) =>
                          sum(b[1], (r) => r.sale) - sum(a[1], (r) => r.sale)
                      )
                      .map(([s, l]) => (
                        <React.Fragment key={`${g.key}-${s}`}>
                          {summaryRow(subformatLabel(s), l, 6, {
                            bg: theme.palette.grey[50],
                          })}
                        </React.Fragment>
                      ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </Box>
        {groups.length === 0 && (
          <Note>No hay líneas con precio de venta en {data.year}.</Note>
        )}
      </Section>

      <Section
        title="A cuánto se vende a cada cliente"
        subtitle="Precio medio que paga cada cliente por cada producto. Pulsa un formato para ver sus subformatos; pasa el ratón por una celda para ver cuántas líneas y su rango."
      >
        <TableContainer sx={{ maxHeight: "78vh" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{ position: "sticky", left: 0, zIndex: 3, minWidth: 240 }}
                >
                  Producto
                </TableCell>
                <TableCell align="center" sx={{ minWidth: 92 }}>
                  Precio medio
                </TableCell>
                {clients.map((c) => (
                  <TableCell
                    key={c}
                    sx={{
                      verticalAlign: "bottom",
                      height: 180,
                      px: 0.5,
                      minWidth: 56,
                    }}
                  >
                    <Box
                      component="span"
                      title={c}
                      sx={{
                        writingMode: "vertical-rl",
                        transform: "rotate(180deg)",
                        whiteSpace: "nowrap",
                        display: "inline-block",
                        maxHeight: 170,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {c}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((g) => (
                <React.Fragment key={g.key}>
                  {matrixRow(
                    <>
                      <Caret open={openMatrix.has(g.key)} />
                      <ChannelSwatch channel={g.channel} />
                      <strong>{g.channel}</strong>{" "}
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                      >
                        {modelLabel(g.objective)}
                      </Typography>
                    </>,
                    g.list,
                    1,
                    g.key,
                    undefined,
                    () => toggle(openMatrix, setOpenMatrix, g.key)
                  )}
                  {openMatrix.has(g.key) &&
                    [...groupBy(g.list, (r) => r.subformat).entries()].map(
                      ([s, l]) =>
                        matrixRow(
                          subformatLabel(s),
                          l,
                          5,
                          `${g.key}-${s}`,
                          theme.palette.grey[50]
                        )
                    )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Section>

      <Section
        title={`Datos a revisar (${review.length} líneas)`}
        subtitle="Líneas apartadas de los precios porque su precio es más de 3 veces mayor o menor que el habitual de su subformato. Pueden ser errores de carga o productos mal clasificados: conviene revisarlas en el briefing."
        action={
          review.length > 0 ? (
            <Link
              component="button"
              underline="hover"
              onClick={() => setShowReview((v) => !v)}
            >
              {showReview ? "Ocultar" : "Ver"}
            </Link>
          ) : undefined
        }
      >
        <Collapse in={showReview} unmountOnExit>
          <TableContainer sx={{ maxHeight: "60vh" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Mes</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Línea</TableCell>
                  <TableCell>Producto</TableCell>
                  <TableCell>Técnico</TableCell>
                  <Num>Precio</Num>
                  <Num>Precio habitual</Num>
                </TableRow>
              </TableHead>
              <TableBody>
                {review.map((r) => (
                  <TableRow key={r.lineId} hover>
                    <TableCell>{MONTHS_SHORT[r.month]}</TableCell>
                    <TableCell>{r.client}</TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 320,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={`${r.lineName} · ${r.briefingName}`}
                    >
                      {r.lineName}
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>
                      {r.channel} · {subformatLabel(r.subformat)} ·{" "}
                      {r.objective}
                    </TableCell>
                    <TableCell>
                      {r.technicians.join(", ") || "Sin asignar"}
                    </TableCell>
                    <Num sx={{ fontWeight: 600 }}>
                      {unitMoney(r.grossUnitCost)}
                    </Num>
                    <Num muted>{unitMoney(habitual(r))}</Num>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Collapse>
        {review.length === 0 && <Note>Ningún precio se sale de escala.</Note>}
      </Section>
    </>
  );
};

export default PricesTab;
